import pluralize from 'pluralize';

import { ClipType } from '@air/api/types';

import { type ItemPickerItemType, type ItemPickerNavView } from '~/components/ItemPicker/itemPickerTypes';

/**
 * Every clip type except nonMedia — mirrors the ASSET_TYPES constant in
 * ~/swr-hooks/gallery/galleryView/utilsCollection.ts.
 */
export const ITEM_PICKER_ASSET_CLIP_TYPES = Object.values(ClipType).filter((type) => type !== ClipType.nonMedia);

const SINGLE_ITEM_TYPE_NOUNS: Record<ItemPickerItemType, string> = {
  asset: 'asset',
  board: 'board',
  file: 'file',
};

export const getItemPickerNavLabel = (view: ItemPickerNavView): string => {
  switch (view.kind) {
    case 'all':
      return 'Search';
    case 'favorites':
      return 'My favorites';
    case 'recent':
      return 'Recently added';
    default:
      return view.label;
  }
};

export type GetItemPickerClipTypesParams = {
  itemTypes: ItemPickerItemType[];
  assetTypes?: ClipType[];
};

export const getItemPickerClipTypes = ({ itemTypes, assetTypes }: GetItemPickerClipTypesParams): ClipType[] => {
  const clipTypes: ClipType[] = [];

  if (itemTypes.includes('asset')) {
    clipTypes.push(...(assetTypes?.length ? assetTypes : ITEM_PICKER_ASSET_CLIP_TYPES));
  }

  if (itemTypes.includes('file') && !clipTypes.includes(ClipType.nonMedia)) {
    clipTypes.push(ClipType.nonMedia);
  }

  return clipTypes;
};

export type GetItemPickerConfirmLabelParams = {
  allowMultiple: boolean;
  /** When provided, used verbatim regardless of selection — see ItemPickerProps['confirmLabel']. */
  confirmLabel?: string;
  destinationLabel?: string;
  itemTypes: ItemPickerItemType[];
  selectedCount: number;
};

export const getItemPickerConfirmLabel = ({
  allowMultiple,
  confirmLabel,
  destinationLabel,
  itemTypes,
  selectedCount,
}: GetItemPickerConfirmLabelParams): string => {
  if (confirmLabel) {
    return confirmLabel;
  }

  const defaultLabel = destinationLabel ? `Add to ${destinationLabel}` : 'Add items';

  if (!allowMultiple || selectedCount === 0) {
    return defaultLabel;
  }

  const noun = itemTypes.length === 1 ? SINGLE_ITEM_TYPE_NOUNS[itemTypes[0]] : 'item';

  return `Add ${pluralize(noun, selectedCount, true)}`;
};
